import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';
import Page from 'components/Page';
import ThanksModal from 'modules/Programs/Program/ThanksModal';
import store from 'store';

import { UserActionTypes } from '@common/api/users';
import { ROLE, checkRole } from '@common/components/RBAC';
import ServiceRouter, { useServiceRouter } from '@common/components/ServiceRouter';
import useModel from '@common/helpers/useModel';
import StudentModel, { IStudentModel } from '@common/models/Users/Students/Student';

import useGetProgramModel from '../hooks/useGetProgramModel';
import ProgramCollapseButton from './CollapseButton';
import ProgramGrades from './Grades';
import ProgramLessons from './Lessons';
import ProgramSectionCourseInfo from './ProgramSectionCourseInfo';

function ProgramPage() {
	const { programModel } = useGetProgramModel();
	const { t } = useTranslation();
	const [showModal, setShowModal] = useState(false);
	const { checkService } = useServiceRouter();

	const navigate = useNavigate();
	const handleGoBack = () => {
		navigate('/programs');
	};

	const [isCollapsed, setIsCollapsed] = useState(true);
	const handleToggleCollapse = () => {
		setIsCollapsed((collapsed) => !collapsed);
	};

	const isDemoUser = checkRole(ROLE.DEMO_TIS);
	const isPartner = checkRole(ROLE.PARTNER);

	const { profile } = store;
	const studentId = profile.student?.id;

	const student: IStudentModel | null = studentId
		? useModel(StudentModel, { id: studentId }, [studentId])
		: null;

	const handleOpenModal = () => {
		setShowModal(true);
	};

	const commitFirstInteraction = async () => {
		await profile.commitUserAction({ actionType: UserActionTypes.FirstInteraction });
	};

	useEffect(() => {
		const hasFirstInterection = profile.actions.some(
			(item) => item.action === UserActionTypes.FirstInteraction,
		);
		if (!hasFirstInterection) {
			commitFirstInteraction();
		}
	}, [profile]);

	function renderHeader() {
		if (isPartner) return null;

		if (!programModel.loading && !checkService('talented')) {
			return isDemoUser && !student?.phone ? (
				<Button
					variant="contained"
					sx={{ width: '280px', fontSize: '13px', padding: '10px 16px', height: 'auto' }}
					onClick={handleOpenModal}
				>
					{t('program.moreLessons')}
				</Button>
			) : (
				<ServiceRouter learningCenter={null}>
					<ProgramCollapseButton isCollapsed={isCollapsed} onClick={handleToggleCollapse} />
				</ServiceRouter>
			);
		}

		return null;
	}

	return (
		<>
			<Page
				type="list"
				sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
				isLoading={programModel.loading}
				title={programModel.program_name}
				onGoBack={handleGoBack}
				header={renderHeader()}
			>
				<ProgramSectionCourseInfo programModel={programModel} isCollapsed={!isCollapsed} />
				{programModel.is_hide_progress ? (
					<ProgramGrades programModel={programModel} />
				) : (
					<ProgramLessons programModel={programModel} />
				)}
			</Page>
			{showModal && (
				<ThanksModal open={showModal} onClose={() => setShowModal(false)} student={student} />
			)}
		</>
	);
}

export default observer(ProgramPage);
